"use client";

import { c as _c } from "react/compiler-runtime";
import { $convertFromMarkdownString, type Transformer } from "@lexical/markdown";
import type { InitialConfigType } from "@lexical/react/LexicalComposer";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { $createParagraphNode, $createTextNode, $getRoot, $isElementNode, COMMAND_PRIORITY_HIGH, KEY_ENTER_COMMAND, KEY_ESCAPE_COMMAND, type EditorState, type LexicalNode, type RootNode, type TextNode } from "lexical";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { $isCodeNode, CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import ExampleTheme from "@/components/drops/create/lexical/ExampleTheme";
import { EmojiNode } from "@/components/drops/create/lexical/nodes/EmojiNode";
import { HashtagNode } from "@/components/drops/create/lexical/nodes/HashtagNode";
import { $createWaveMentionNode, WaveMentionNode } from "@/components/drops/create/lexical/nodes/WaveMentionNode";
import { $createMentionNode, MentionNode } from "@/components/drops/create/lexical/nodes/MentionNode";
import { GroupMentionNode } from "@/components/drops/create/lexical/nodes/GroupMentionNode";
import EmojiPlugin from "@/components/drops/create/lexical/plugins/emoji/EmojiPlugin";
import type { NewMentionsPluginHandles } from "@/components/drops/create/lexical/plugins/mentions/MentionsPlugin";
import NewMentionsPlugin from "@/components/drops/create/lexical/plugins/mentions/MentionsPlugin";
import type { NewWaveMentionsPluginHandles } from "@/components/drops/create/lexical/plugins/waves/WaveMentionsPlugin";
import NewWaveMentionsPlugin from "@/components/drops/create/lexical/plugins/waves/WaveMentionsPlugin";
import PlainTextPastePlugin from "@/components/drops/create/lexical/plugins/PlainTextPastePlugin";
import { HASHTAG_TRANSFORMER } from "@/components/drops/create/lexical/transformers/HastagTransformer";
import { SAFE_MARKDOWN_TRANSFORMERS_WITHOUT_CODE } from "@/components/drops/create/lexical/transformers/markdownTransformers";
import { MENTION_TRANSFORMER } from "@/components/drops/create/lexical/transformers/MentionTransformer";
import { GROUP_MENTION_TRANSFORMER } from "@/components/drops/create/lexical/transformers/GroupMentionTransformer";
import { getMentionedGroupsFromEditorState } from "@/components/drops/create/lexical/utils/groupMentionDetection";
import { WAVE_MENTION_TRANSFORMER } from "@/components/drops/create/lexical/transformers/WaveMentionTransformer";
import type { MentionedUser, MentionedWave } from "@/entities/IDrop";
import { ApiDropGroupMention } from "@/generated/models/ApiDropGroupMention";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import CreateDropEmojiPicker from "../CreateDropEmojiPicker";
import { addBlankLinePlaceholders, removeBlankLinePlaceholders } from "./blankLinePlaceholders";
import { exportDropMarkdown, normalizeDropMarkdown } from "./normalizeDropMarkdown";
import { areMentionedGroupsEqual } from "@/helpers/waves/drop-group-mentions";
import { containsDisallowedLink } from "@/components/drops/view/part/dropPartMarkdown/linkPreviewDetection";
interface EditDropLexicalProps {
  readonly initialContent: string;
  readonly initialMentions: ApiDropMentionedUser[];
  readonly initialGroupMentions: ApiDropGroupMention[];
  readonly initialWaveMentions: ApiMentionedWave[];
  readonly canMentionAll: boolean;
  readonly waveId: string | null;
  readonly isSaving: boolean;
  readonly linkRestrictionMessage?: string | null | undefined;
  readonly onSave: (content: string, mentions: ApiDropMentionedUser[], mentionedGroups: ApiDropGroupMention[], mentionedWaves: ApiMentionedWave[]) => void;
  readonly onCancel: () => void;
}
const MAX_MENTION_RECONSTRUCTION_PASSES = 20;
const BASE_EDIT_MARKDOWN_TRANSFORMERS = [...SAFE_MARKDOWN_TRANSFORMERS_WITHOUT_CODE, MENTION_TRANSFORMER, HASHTAG_TRANSFORMER, WAVE_MENTION_TRANSFORMER];
const convertCodeNodesToFences = (root: RootNode) => {
  const stack: LexicalNode[] = [...root.getChildren()];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) continue;
    if ($isCodeNode(node)) {
      const language = node.getLanguage() ?? "";
      const safeLanguage = language.trim().replaceAll(/[`\n\r]/g, "");
      const codeText = node.getTextContent();
      const normalizedCode = codeText.endsWith("\n") ? codeText : `${codeText}\n`;
      const maxExistingFence = (codeText.match(/`+/g) ?? []).reduce((max, match) => Math.max(max, match.length), 0);
      const fenceLength = Math.max(3, maxExistingFence + 1);
      const fence = "`".repeat(fenceLength);
      const openFence = safeLanguage ? `${fence}${safeLanguage}` : fence;
      const fencedMarkdown = `${openFence}\n${normalizedCode}${fence}`;
      const paragraph = $createParagraphNode();
      paragraph.append($createTextNode(fencedMarkdown));
      node.replace(paragraph);
      continue;
    }
    if ($isElementNode(node)) {
      stack.push(...node.getChildren());
    }
  }
};
function reconstructSplitMention(currentNode: any, nextNode: any, mentionStart: RegExpMatchArray, mentionEnd: RegExpMatchArray) {
  const fullMention = mentionStart[0] + mentionEnd[0];
  const mentionRegex = /@\[(\w+)\]/;
  const mentionMatch = mentionRegex.exec(fullMention);
  if (!mentionMatch) return false;
  const handle = mentionMatch[1];
  if (!handle) return false;
  const mentionNode = $createMentionNode(`@${handle}`);
  const currentText = currentNode.getTextContent();
  const nextText = nextNode.getTextContent();
  const beforeMention = currentText.substring(0, currentText.length - mentionStart[0].length);
  const afterMention = nextText.substring(mentionEnd[0].length);
  if (beforeMention) {
    currentNode.setTextContent(beforeMention);
    currentNode.insertAfter(mentionNode);
  } else {
    currentNode.remove();
    nextNode.insertBefore(mentionNode);
  }
  if (afterMention) {
    nextNode.setTextContent(afterMention);
  } else {
    nextNode.remove();
  }
  return true;
}
function reconstructSplitWaveMention(currentNode: any, nextNode: any, mentionStart: RegExpMatchArray, mentionEnd: RegExpMatchArray) {
  const fullMention = mentionStart[0] + mentionEnd[0];
  const mentionRegex = /#\[([^\]]+)\]/;
  const mentionMatch = mentionRegex.exec(fullMention);
  if (!mentionMatch) return false;
  const waveName = mentionMatch[1];
  const mentionNode = $createWaveMentionNode(`#${waveName}`);
  const currentText = currentNode.getTextContent();
  const nextText = nextNode.getTextContent();
  const beforeMention = currentText.substring(0, currentText.length - mentionStart[0].length);
  const afterMention = nextText.substring(mentionEnd[0].length);
  if (beforeMention) {
    currentNode.setTextContent(beforeMention);
    currentNode.insertAfter(mentionNode);
  } else {
    currentNode.remove();
    nextNode.insertBefore(mentionNode);
  }
  if (afterMention) {
    nextNode.setTextContent(afterMention);
  } else {
    nextNode.remove();
  }
  return true;
}
function processSplitMentions(textNodes: Array<TextNode>): boolean {
  for (let i = 0; i < textNodes.length - 1; i++) {
    const currentNode = textNodes[i];
    const nextNode = textNodes[i + 1];
    const currentText = currentNode?.getTextContent();
    const nextText = nextNode?.getTextContent();
    const mentionStart = currentText?.match(/@\[\w*$/);
    const mentionEnd = nextText?.match(/^\w*\]/);
    if (mentionStart && mentionEnd) {
      try {
        if (reconstructSplitMention(currentNode, nextNode, mentionStart, mentionEnd)) {
          return true;
        }
      } catch (error) {
        console.warn("Failed to reconstruct split mention", error);
      }
    }
    const waveMentionStart = currentText?.match(/#\[[^\]]*$/);
    const waveMentionEnd = nextText?.match(/^[^\]]*\]/);
    if (waveMentionStart && waveMentionEnd) {
      try {
        if (reconstructSplitWaveMention(currentNode, nextNode, waveMentionStart, waveMentionEnd)) {
          return true;
        }
      } catch (error) {
        console.warn("Failed to reconstruct split wave mention", error);
      }
    }
  }
  return false;
}
function InitialContentPlugin(t0) {
  const $ = _c(5);
  const {
    initialContent,
    transformers
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  let t2;
  if ($[0] !== editor || $[1] !== initialContent || $[2] !== transformers) {
    t1 = () => {
      editor.update(() => {
        const normalizedContent = normalizeDropMarkdown(initialContent);
        $convertFromMarkdownString(normalizedContent, transformers);
        const root = $getRoot();
        convertCodeNodesToFences(root);
        let needsAnotherPass = true;
        let passCount = 0;
        while (needsAnotherPass && passCount < MAX_MENTION_RECONSTRUCTION_PASSES) {
          const textNodes = root.getAllTextNodes();
          const hasUnprocessedMentions = textNodes.some(_temp);
          if (hasUnprocessedMentions) {
            break;
          }
          needsAnotherPass = processSplitMentions(textNodes);
          passCount = passCount + 1;
        }
        if (needsAnotherPass && passCount >= MAX_MENTION_RECONSTRUCTION_PASSES) {
          console.warn("Mention reconstruction reached max passes without converging");
        }
        root.selectEnd();
      });
    };
    t2 = [editor, initialContent, transformers];
    $[0] = editor;
    $[1] = initialContent;
    $[2] = transformers;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  return null;
}
function _temp(node) {
  const text = node.getTextContent();
  return /@\[\w+\]/.test(text) || /#\[[^\]]+\]/.test(text);
}
function FocusPlugin(t0) {
  const $ = _c(4);
  const {
    isApp
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  let t2;
  if ($[0] !== editor || $[1] !== isApp) {
    t1 = () => {
      const focusEditor = () => {
        editor.focus();
        requestAnimationFrame(_temp2);
      };
      if (isApp) {
        const timeouts = [setTimeout(focusEditor, 100), setTimeout(focusEditor, 350), setTimeout(focusEditor, 600)];
        return () => timeouts.forEach(clearTimeout);
      } else {
        focusEditor();
      }
    };
    t2 = [editor, isApp];
    $[0] = editor;
    $[1] = isApp;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function _temp2() {
  const contentEditable = document.querySelector("[contenteditable=\"true\"]") as HTMLElement;
  if (contentEditable && document.activeElement !== contentEditable) {
    contentEditable.focus();
    contentEditable.click();
  }
}
function KeyboardPlugin(t0) {
  const $ = _c(29);
  const {
    onSave,
    onCancel,
    isSaving,
    isSaveBlocked,
    isMobileOrApp,
    initialContent,
    initialGroupMentions,
    canResolveAllGroupMention,
    transformers,
    mentionsRef,
    waveMentionsRef
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  if ($[0] !== initialContent) {
    t1 = removeBlankLinePlaceholders(initialContent);
    $[0] = initialContent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sanitizedInitialContent = t1;
  let t2;
  if ($[2] !== canResolveAllGroupMention || $[3] !== editor || $[4] !== initialGroupMentions || $[5] !== isMobileOrApp || $[6] !== isSaveBlocked || $[7] !== isSaving || $[8] !== mentionsRef || $[9] !== onCancel || $[10] !== onSave || $[11] !== sanitizedInitialContent || $[12] !== transformers || $[13] !== waveMentionsRef) {
    t2 = () => {
      const removeEscapeListener = editor.registerCommand(KEY_ESCAPE_COMMAND, () => {
        onCancel();
        return true;
      }, COMMAND_PRIORITY_HIGH);
      const removeEnterListener = editor.registerCommand(KEY_ENTER_COMMAND, event => {
        if (mentionsRef.current?.isMentionsOpen() || waveMentionsRef.current?.isWaveMentionsOpen()) {
          return false;
        }
        if (isMobileOrApp) {
          return true;
        }
        if (event?.shiftKey) {
          return false;
        }
        if (!isSaving && !isSaveBlocked) {
          const currentMarkdown = exportDropMarkdown(editor.getEditorState(), transformers);
          const sanitizedCurrentMarkdown = removeBlankLinePlaceholders(currentMarkdown);
          const currentMentionedGroups = getMentionedGroupsFromEditorState(editor.getEditorState(), canResolveAllGroupMention);
          if (sanitizedCurrentMarkdown.trim() === sanitizedInitialContent.trim() && areMentionedGroupsEqual(currentMentionedGroups, initialGroupMentions)) {
            onCancel();
          } else {
            onSave();
          }
        }
        return true;
      }, COMMAND_PRIORITY_HIGH);
      return () => {
        removeEscapeListener();
        removeEnterListener();
      };
    };
    $[2] = canResolveAllGroupMention;
    $[3] = editor;
    $[4] = initialGroupMentions;
    $[5] = isMobileOrApp;
    $[6] = isSaveBlocked;
    $[7] = isSaving;
    $[8] = mentionsRef;
    $[9] = onCancel;
    $[10] = onSave;
    $[11] = sanitizedInitialContent;
    $[12] = transformers;
    $[13] = waveMentionsRef;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== canResolveAllGroupMention || $[16] !== editor || $[17] !== initialContent || $[18] !== initialGroupMentions || $[19] !== isMobileOrApp || $[20] !== isSaveBlocked || $[21] !== isSaving || $[22] !== mentionsRef || $[23] !== onCancel || $[24] !== onSave || $[25] !== sanitizedInitialContent || $[26] !== transformers || $[27] !== waveMentionsRef) {
    t3 = [editor, onSave, onCancel, isSaving, isSaveBlocked, isMobileOrApp, initialContent, initialGroupMentions, canResolveAllGroupMention, transformers, mentionsRef, waveMentionsRef, sanitizedInitialContent];
    $[15] = canResolveAllGroupMention;
    $[16] = editor;
    $[17] = initialContent;
    $[18] = initialGroupMentions;
    $[19] = isMobileOrApp;
    $[20] = isSaveBlocked;
    $[21] = isSaving;
    $[22] = mentionsRef;
    $[23] = onCancel;
    $[24] = onSave;
    $[25] = sanitizedInitialContent;
    $[26] = transformers;
    $[27] = waveMentionsRef;
    $[28] = t3;
  } else {
    t3 = $[28];
  }
  useEffect(t2, t3);
  return null;
}
const EditDropLexical: React.FC<EditDropLexicalProps> = t0 => {
  const $ = _c(82);
  const {
    initialContent,
    initialMentions,
    initialGroupMentions,
    initialWaveMentions,
    canMentionAll,
    waveId,
    isSaving,
    linkRestrictionMessage: t1,
    onSave,
    onCancel
  } = t0;
  const linkRestrictionMessage = t1 === undefined ? null : t1;
  const [editorState, setEditorState] = useState(null);
  const [mentionedUsers, setMentionedUsers] = useState(initialMentions);
  const [mentionedWaves, setMentionedWaves] = useState(initialWaveMentions);
  const editorRef = useRef(null);
  const mentionsRef = useRef(null);
  const waveMentionsRef = useRef(null);
  const {
    isApp,
    isMobileDevice
  } = useDeviceInfo();
  const isMobileOrApp = isMobileDevice || isApp;
  let t2;
  if ($[0] !== initialContent) {
    t2 = normalizeDropMarkdown(initialContent);
    $[0] = initialContent;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const normalizedInitialContent = t2;
  let t3;
  if ($[2] !== normalizedInitialContent) {
    t3 = addBlankLinePlaceholders(normalizedInitialContent);
    $[2] = normalizedInitialContent;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const editorInitialContent = t3;
  let t4;
  if ($[4] !== initialGroupMentions) {
    t4 = initialGroupMentions.includes(ApiDropGroupMention.All);
    $[4] = initialGroupMentions;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const hasInitialAllGroupMention = t4;
  const canResolveAllGroupMention = canMentionAll || hasInitialAllGroupMention;
  let t5;
  if ($[6] !== hasInitialAllGroupMention) {
    t5 = hasInitialAllGroupMention ? [...BASE_EDIT_MARKDOWN_TRANSFORMERS, GROUP_MENTION_TRANSFORMER] : BASE_EDIT_MARKDOWN_TRANSFORMERS;
    $[6] = hasInitialAllGroupMention;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const importMarkdownTransformers = t5;
  let t6;
  if ($[8] !== canResolveAllGroupMention) {
    t6 = canResolveAllGroupMention ? [...BASE_EDIT_MARKDOWN_TRANSFORMERS, GROUP_MENTION_TRANSFORMER] : BASE_EDIT_MARKDOWN_TRANSFORMERS;
    $[8] = canResolveAllGroupMention;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const exportMarkdownTransformers = t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      namespace: "EditDropLexical",
      theme: ExampleTheme,
      onError: _temp3,
      nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode, CodeHighlightNode, HorizontalRuleNode, AutoLinkNode, LinkNode, MentionNode, GroupMentionNode, HashtagNode, WaveMentionNode, EmojiNode]
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const initialConfig = t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = editorState_0 => {
      setEditorState(editorState_0);
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleEditorChange = t8;
  let t9;
  bb0: {
    if (!editorState) {
      t9 = normalizedInitialContent;
      break bb0;
    }
    let t10;
    if ($[12] !== editorState || $[13] !== exportMarkdownTransformers) {
      t10 = removeBlankLinePlaceholders(exportDropMarkdown(editorState, exportMarkdownTransformers));
      $[12] = editorState;
      $[13] = exportMarkdownTransformers;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    t9 = t10;
  }
  const currentMarkdown = t9;
  let t10;
  if ($[15] !== currentMarkdown || $[16] !== linkRestrictionMessage) {
    t10 = !!linkRestrictionMessage && containsDisallowedLink(currentMarkdown);
    $[15] = currentMarkdown;
    $[16] = linkRestrictionMessage;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const isSaveBlockedByLinks = t10;
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = user => {
      const newMention = {
        mentioned_profile_id: user.mentioned_profile_id,
        handle_in_content: user.handle_in_content
      };
      setMentionedUsers(prev => {
        if (prev.some(m => m.mentioned_profile_id === newMention.mentioned_profile_id)) {
          return prev;
        }
        return [...prev, newMention];
      });
    };
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const handleMentionSelect = t11;
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = wave => {
      const newMention_0 = {
        wave_id: wave.wave_id,
        wave_name_in_content: wave.wave_name_in_content
      };
      setMentionedWaves(prev_0 => {
        if (prev_0.some(m_0 => m_0.wave_id === newMention_0.wave_id)) {
          return prev_0;
        }
        return [...prev_0, newMention_0];
      });
    };
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const handleWaveMentionSelect = t12;
  let t13;
  if ($[20] !== canResolveAllGroupMention || $[21] !== currentMarkdown || $[22] !== editorState || $[23] !== initialGroupMentions || $[24] !== isSaveBlockedByLinks || $[25] !== mentionedUsers || $[26] !== mentionedWaves || $[27] !== normalizedInitialContent || $[28] !== onCancel || $[29] !== onSave) {
    t13 = () => {
      if (!editorState) {
        return;
      }
      if (isSaveBlockedByLinks) {
        return;
      }
      const sanitizedMarkdown = currentMarkdown;
      const sanitizedMentionedGroups = getMentionedGroupsFromEditorState(editorState, canResolveAllGroupMention);
      if (sanitizedMarkdown.trim() === normalizedInitialContent.trim() && areMentionedGroupsEqual(sanitizedMentionedGroups, initialGroupMentions)) {
        onCancel();
        return;
      }
      onSave(sanitizedMarkdown, mentionedUsers, sanitizedMentionedGroups, mentionedWaves);
    };
    $[20] = canResolveAllGroupMention;
    $[21] = currentMarkdown;
    $[22] = editorState;
    $[23] = initialGroupMentions;
    $[24] = isSaveBlockedByLinks;
    $[25] = mentionedUsers;
    $[26] = mentionedWaves;
    $[27] = normalizedInitialContent;
    $[28] = onCancel;
    $[29] = onSave;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const handleSave = t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <RichTextPlugin contentEditable={<div className="tw-relative"><ContentEditable className="tw-min-h-[40px] tw-w-full tw-resize-none tw-overflow-y-auto tw-overflow-x-hidden tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800 tw-py-2.5 tw-pl-3 tw-pr-10 tw-text-sm tw-text-iron-100 tw-outline-none tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 focus:tw-border-primary-400 desktop-hover:hover:tw-scrollbar-thumb-iron-300" style={{
        fontFamily: "inherit",
        lineHeight: "1.4"
      }} /><CreateDropEmojiPicker top="tw-top-1" /></div>} placeholder={<div className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-2 tw-text-sm tw-text-iron-500">Edit message...</div>} ErrorBoundary={LexicalErrorBoundary} />;
    t15 = <OnChangePlugin onChange={handleEditorChange} />;
    t16 = <HistoryPlugin />;
    t17 = <PlainTextPastePlugin />;
    t18 = <MarkdownShortcutPlugin transformers={BASE_EDIT_MARKDOWN_TRANSFORMERS} />;
    t19 = <ListPlugin />;
    t20 = <LinkPlugin />;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
  } else {
    t14 = $[31];
    t15 = $[32];
    t16 = $[33];
    t17 = $[34];
    t18 = $[35];
    t19 = $[36];
    t20 = $[37];
  }
  let t21;
  if ($[38] !== canMentionAll || $[39] !== waveId) {
    t21 = <NewMentionsPlugin ref={mentionsRef} waveId={waveId} onSelect={handleMentionSelect} canMentionAll={canMentionAll} />;
    $[38] = canMentionAll;
    $[39] = waveId;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  let t23;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <NewWaveMentionsPlugin ref={waveMentionsRef} onSelect={handleWaveMentionSelect} />;
    t23 = <EmojiPlugin />;
    $[41] = t22;
    $[42] = t23;
  } else {
    t22 = $[41];
    t23 = $[42];
  }
  let t24;
  if ($[43] !== editorInitialContent || $[44] !== importMarkdownTransformers) {
    t24 = <InitialContentPlugin initialContent={editorInitialContent} transformers={importMarkdownTransformers} />;
    $[43] = editorInitialContent;
    $[44] = importMarkdownTransformers;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== isApp) {
    t25 = <FocusPlugin isApp={isApp} />;
    $[46] = isApp;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== canResolveAllGroupMention || $[49] !== exportMarkdownTransformers || $[50] !== handleSave || $[51] !== initialGroupMentions || $[52] !== isMobileOrApp || $[53] !== isSaveBlockedByLinks || $[54] !== isSaving || $[55] !== normalizedInitialContent || $[56] !== onCancel) {
    t26 = <KeyboardPlugin onSave={handleSave} onCancel={onCancel} isSaving={isSaving} isSaveBlocked={isSaveBlockedByLinks} isMobileOrApp={isMobileOrApp} initialContent={normalizedInitialContent} initialGroupMentions={initialGroupMentions} canResolveAllGroupMention={canResolveAllGroupMention} transformers={exportMarkdownTransformers} mentionsRef={mentionsRef} waveMentionsRef={waveMentionsRef} />;
    $[48] = canResolveAllGroupMention;
    $[49] = exportMarkdownTransformers;
    $[50] = handleSave;
    $[51] = initialGroupMentions;
    $[52] = isMobileOrApp;
    $[53] = isSaveBlockedByLinks;
    $[54] = isSaving;
    $[55] = normalizedInitialContent;
    $[56] = onCancel;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t21 || $[59] !== t24 || $[60] !== t25 || $[61] !== t26) {
    t27 = <LexicalComposer initialConfig={initialConfig}><div ref={editorRef} className="tw-relative">{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t24}{t25}{t26}</div></LexicalComposer>;
    $[58] = t21;
    $[59] = t24;
    $[60] = t25;
    $[61] = t26;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== handleSave || $[64] !== isApp || $[65] !== isMobileDevice || $[66] !== isSaveBlockedByLinks || $[67] !== isSaving || $[68] !== linkRestrictionMessage || $[69] !== onCancel) {
    t28 = !isApp && <div className="tw-mt-1 tw-flex tw-items-center tw-text-xs tw-text-iron-400">{!isMobileDevice && <>escape to </>}<button onClick={onCancel} className="tw-cursor-pointer tw-rounded-md tw-border-0 tw-bg-transparent tw-px-[3px] tw-font-medium tw-text-primary-400 tw-transition focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 desktop-hover:hover:tw-underline">cancel</button>{" "}{isMobileDevice ? "\u2022 " : "\u2022 enter to "}<button onClick={handleSave} disabled={isSaveBlockedByLinks || isSaving} title={isSaveBlockedByLinks ? linkRestrictionMessage : undefined} className={`tw-rounded-md tw-border-0 tw-bg-transparent tw-px-[3px] tw-font-medium tw-text-primary-400 tw-transition focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 ${isSaveBlockedByLinks || isSaving ? "tw-cursor-not-allowed tw-opacity-50" : "tw-cursor-pointer desktop-hover:hover:tw-underline"}`}>save</button></div>;
    $[63] = handleSave;
    $[64] = isApp;
    $[65] = isMobileDevice;
    $[66] = isSaveBlockedByLinks;
    $[67] = isSaving;
    $[68] = linkRestrictionMessage;
    $[69] = onCancel;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== handleSave || $[72] !== isApp || $[73] !== isSaveBlockedByLinks || $[74] !== isSaving || $[75] !== linkRestrictionMessage || $[76] !== onCancel) {
    t29 = isApp && <div className="tw-mb-2 tw-mt-3"><div className="tw-flex tw-gap-x-2"><button onClick={onCancel} disabled={isSaving} className="tw-rounded-lg tw-border-0 tw-bg-iron-800 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-iron-300 tw-transition-colors tw-duration-150 active:tw-bg-iron-700 disabled:tw-opacity-50">Cancel</button><button onClick={handleSave} disabled={isSaving || isSaveBlockedByLinks} title={isSaveBlockedByLinks ? linkRestrictionMessage : undefined} className="tw-rounded-lg tw-border-0 tw-bg-primary-500 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white tw-transition-colors tw-duration-150 active:tw-bg-primary-600 disabled:tw-opacity-50">{isSaving ? "Saving..." : "Save"}</button></div></div>;
    $[71] = handleSave;
    $[72] = isApp;
    $[73] = isSaveBlockedByLinks;
    $[74] = isSaving;
    $[75] = linkRestrictionMessage;
    $[76] = onCancel;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] !== t27 || $[79] !== t28 || $[80] !== t29) {
    t30 = <div className="tw-w-full" data-editor-mode="true">{t27}{t28}{t29}</div>;
    $[78] = t27;
    $[79] = t28;
    $[80] = t29;
    $[81] = t30;
  } else {
    t30 = $[81];
  }
  return t30;
};
export default EditDropLexical;
function _temp3(error) {
  console.error(error);
}